import { useRef, useState } from "react";
import { X, Upload, CheckCircle2, AlertCircle, Table2, MessageSquare, FileDown } from "lucide-react";
import { bulkCreateWorkflows } from "@/app/lib/auditflowApi";
import type { AuditFlowWorkflow } from "../shared/types";

interface ImportWorkflow {
    title: string;
    type: "assistant" | "tabular";
    practice?: string | null;
    prompt_md?: string;
    columns_config?: { index: number; name: string; prompt: string; format?: string; tags?: string[] }[];
}

interface ImportResult {
    title: string;
    status: "ok" | "error";
    error?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onImported: (workflows: AuditFlowWorkflow[]) => void;
}

export function BulkImportWorkflowsModal({ open, onClose, onImported }: Props) {
    const fileRef = useRef<HTMLInputElement>(null);
    const [parsed, setParsed] = useState<ImportWorkflow[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [results, setResults] = useState<ImportResult[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);

    if (!open) return null;

    function reset() {
        setParsed(null);
        setParseError(null);
        setResults(null);
        setFileName(null);
        if (fileRef.current) fileRef.current.value = "";
    }

    function handleClose() {
        reset();
        onClose();
    }

    function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setParseError(null);
        setParsed(null);
        setResults(null);

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const json = JSON.parse(ev.target?.result as string);
                if (!Array.isArray(json?.workflows) || json.workflows.length === 0) {
                    setParseError('Invalid format: expected { "workflows": [...] } with at least one entry.');
                    return;
                }
                const workflows = json.workflows as ImportWorkflow[];
                const invalid = workflows.findIndex(
                    (wf) => !wf.title?.trim() || !["assistant", "tabular"].includes(wf.type)
                );
                if (invalid !== -1) {
                    setParseError(
                        `Workflow at index ${invalid} ("${workflows[invalid]?.title ?? ""}") is missing a valid title or type.`
                    );
                    return;
                }
                setParsed(workflows);
            } catch {
                setParseError("Could not parse file. Make sure it is valid JSON.");
            }
        };
        reader.readAsText(file);
    }

    async function handleImport() {
        if (!parsed) return;
        setLoading(true);
        try {
            const { created, errors } = await bulkCreateWorkflows(parsed);
            const errorMap = new Map(errors.map((e) => [e.index, e.error]));
            const res: ImportResult[] = parsed.map((wf, i) => ({
                title: wf.title,
                status: errorMap.has(i) ? "error" : "ok",
                error: errorMap.get(i),
            }));
            setResults(res);
            if (created.length > 0) onImported(created as AuditFlowWorkflow[]);
        } catch (err) {
            setParseError((err as Error).message || "Import failed.");
        } finally {
            setLoading(false);
        }
    }

    const successCount = results?.filter((r) => r.status === "ok").length ?? 0;
    const errorCount = results?.filter((r) => r.status === "error").length ?? 0;
    const allDone = results !== null;

    return (
        <div className="fixed inset-0 z-101 flex items-center justify-center bg-black/20 backdrop-blur-xs">
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "80vh" }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0 border-b border-gray-100">
                    <div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
                            <span>Workflows</span>
                            <span>›</span>
                            <span>Bulk import</span>
                        </div>
                        <h2 className="text-base font-semibold text-gray-900">Import workflows from JSON</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                    {/* File picker */}
                    {!allDone && (
                        <div
                            className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 py-8 cursor-pointer hover:border-gray-400 transition-colors"
                            onClick={() => fileRef.current?.click()}
                        >
                            <Upload className="h-6 w-6 text-gray-400" />
                            <p className="text-sm text-gray-500">
                                {fileName ? (
                                    <span className="font-medium text-gray-700">{fileName}</span>
                                ) : (
                                    "Click to select a JSON import file"
                                )}
                            </p>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".json,application/json"
                                className="hidden"
                                onChange={handleFile}
                            />
                        </div>
                    )}

                    {/* Parse error */}
                    {parseError && (
                        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            {parseError}
                        </div>
                    )}

                    {/* Preview list */}
                    {parsed && !allDone && (
                        <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                                {parsed.length} workflow{parsed.length !== 1 ? "s" : ""} detected
                            </p>
                            <div className="space-y-1">
                                {parsed.map((wf, i) => (
                                    <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 text-sm">
                                        {wf.type === "tabular" ? (
                                            <Table2 className="h-4 w-4 text-gray-400 shrink-0" />
                                        ) : (
                                            <MessageSquare className="h-4 w-4 text-gray-400 shrink-0" />
                                        )}
                                        <span className="flex-1 truncate text-gray-800 font-medium">{wf.title}</span>
                                        {wf.columns_config && (
                                            <span className="text-xs text-gray-400 shrink-0">
                                                {wf.columns_config.length} col{wf.columns_config.length !== 1 ? "s" : ""}
                                            </span>
                                        )}
                                        {wf.practice && (
                                            <span className="text-xs text-gray-400 shrink-0">{wf.practice}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Results */}
                    {allDone && results && (
                        <div>
                            <div className="flex items-center gap-4 mb-3">
                                {successCount > 0 && (
                                    <div className="flex items-center gap-1.5 text-sm text-green-700">
                                        <CheckCircle2 className="h-4 w-4" />
                                        {successCount} imported
                                    </div>
                                )}
                                {errorCount > 0 && (
                                    <div className="flex items-center gap-1.5 text-sm text-red-600">
                                        <AlertCircle className="h-4 w-4" />
                                        {errorCount} failed
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1">
                                {results.map((r, i) => (
                                    <div
                                        key={i}
                                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${
                                            r.status === "ok"
                                                ? "border-green-100 bg-green-50"
                                                : "border-red-100 bg-red-50"
                                        }`}
                                    >
                                        {r.status === "ok" ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                        ) : (
                                            <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                                        )}
                                        <span className={`flex-1 truncate font-medium ${r.status === "ok" ? "text-green-800" : "text-red-700"}`}>
                                            {r.title}
                                        </span>
                                        {r.error && (
                                            <span className="text-xs text-red-500 shrink-0">{r.error}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-6 py-4 shrink-0">
                    {/* Left: See template (hidden after import completes) */}
                    {!allDone ? (
                        <a
                            href="/workflows-import-template.json"
                            download="workflows-import-template.json"
                            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors"
                        >
                            <FileDown className="h-3.5 w-3.5" />
                            See template
                        </a>
                    ) : (
                        <span />
                    )}

                    {/* Right: action buttons */}
                    <div className="flex items-center gap-2">
                        {allDone ? (
                            <button
                                onClick={handleClose}
                                className="rounded-lg bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
                            >
                                Done
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleClose}
                                    className="rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleImport}
                                    disabled={!parsed || loading}
                                    className="rounded-lg bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-40 transition-colors"
                                >
                                    {loading ? "Importing…" : `Import ${parsed ? parsed.length : ""} workflow${parsed?.length !== 1 ? "s" : ""}`}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
